import { t } from '../i18n';
// client/admin-client/src/pages/AssetLedger.jsx
import React from 'react';
import { fetchAssetLedger } from '../api/admin';
const fmtDate = (v) => {
    if (!v)
        return '-';
    try {
        const d = new Date(v);
        return new Intl.DateTimeFormat('ko-KR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        }).format(d);
    }
    catch {
        return String(v);
    }
};
const fmtInt = (n) => new Intl.NumberFormat('ko-KR').format(Number(n || 0));
function TypeBadge({ type }) {
    const map = {
        asset: 'bg-gray-900 text-white',
        rakeback: 'bg-amber-500 text-black'
    };
    const cls = map[type] || 'bg-gray-200 text-gray-700';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{type || 'unknown'}</span>;
}
function ChannelBadge({ channel }) {
    const map = {
        'admin-ui': 'bg-indigo-100 text-indigo-700',
        'game': 'bg-emerald-100 text-emerald-700',
        'api': 'bg-sky-100 text-sky-700'
    };
    const cls = map[channel] || 'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{channel || 'unknown'}</span>;
}
export default function AssetLedger() {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    // 필터/페이지 state
    const [q, setQ] = React.useState('');
    const [assetType, setAssetType] = React.useState('all'); // asset|rakeback|all
    const [from, setFrom] = React.useState(''); // YYYY-MM-DD
    const [to, setTo] = React.useState(''); // YYYY-MM-DD
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [sort, setSort] = React.useState('desc'); // desc|asc
    const [total, setTotal] = React.useState(0);
    const [hasMore, setHasMore] = React.useState(false);
    const load = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = { page, pageSize, q, sort };
            if (assetType && assetType !== 'all')
                params.asset_type = assetType;
            if (from)
                params.from = from;
            if (to)
                params.to = to;
            const data = await fetchAssetLedger(params);
            setRows(data.rows || []);
            setTotal(Number(data.total || 0));
            setHasMore(Boolean(data.hasMore));
        }
        catch (e) {
            setError(e?.response?.data?.message || e.message || t("\uBD88\uB7EC\uC624\uAE30 \uC2E4\uD328"));
        }
        finally {
            setLoading(false);
        }
    }, [page, pageSize, q, assetType, from, to, sort]);
    React.useEffect(() => { load(); }, [load]);
    const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };
    const onReset = () => {
        setQ('');
        setAssetType('all');
        setFrom('');
        setTo('');
        setPage(1);
        setPageSize(20);
        setSort('desc');
    };
    const empty = !loading && !rows.length;
    return (<div className="max-w-[1200px] mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold">{t("\uC790\uC0B0\uAE30\uB85D")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("\uB0B4 \uACC4\uC815\uACFC \uC9C1\u00B7\uAC04\uC811 \uD558\uC704(\uC5D0\uC774\uC804\uD2B8/\uC720\uC800)\uC758 \uC790\uC0B0 \uC774\uB3D9 \uAE30\uB85D\uC785\uB2C8\uB2E4. \uD544\uD130\uB97C \uC801\uC6A9\uD574 \uAC80\uC0C9\uD560 \uC218 \uC788\uC5B4\uC694.")}</p>
      </div>

      {/* 필터 바 */}
      <form onSubmit={onSearch} className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">{t("\uAC80\uC0C9 (id / user_code / nickname / \uBA54\uBAA8)")}</label>
            <input className="w-full border rounded-lg px-3 py-2" placeholder={t("\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD558\uC138\uC694")} value={q} onChange={e => setQ(e.target.value)}/>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t("\uC790\uC0B0 \uD0C0\uC785")}</label>
            <select className="w-full border rounded-lg px-3 py-2" value={assetType} onChange={e => { setAssetType(e.target.value); setPage(1); }}>
              <option value="all">{t("\uC804\uCCB4")}</option>
              <option value="asset">{t("\uC77C\uBC18 \uC790\uC0B0")}</option>
              <option value="rakeback">{t("\uB808\uC774\uD06C\uBC31")}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t("\uC2DC\uC791\uC77C")}</label>
            <input type="date" className="w-full border rounded-lg px-3 py-2" value={from} onChange={e => setFrom(e.target.value)}/>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t("\uC885\uB8CC\uC77C")}</label>
            <input type="date" className="w-full border rounded-lg px-3 py-2" value={to} onChange={e => setTo(e.target.value)}/>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-xs text-gray-600">{t("\uC815\uB82C")}</label>
          <select className="border rounded-lg px-2 py-1 text-sm" value={sort} onChange={e => setSort(e.target.value)}>
            <option value="desc">{t("\uCD5C\uADFC\uC21C")}</option>
            <option value="asc">{t("\uC624\uB798\uB41C\uC21C")}</option>
          </select>

          <label className="ml-4 text-xs text-gray-600">{t("\uD398\uC774\uC9C0 \uD06C\uAE30")}</label>
          <select className="border rounded-lg px-2 py-1 text-sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <div className="ml-auto flex gap-2">
            <button type="button" className="px-3 py-2 rounded-lg border" onClick={onReset}>{t("\uCD08\uAE30\uD654")}</button>
            <button type="submit" className="px-3 py-2 rounded-lg bg-gray-900 text-white">{t("\uAC80\uC0C9")}</button>
          </div>
        </div>
      </form>

      {/* 결과 영역 */}
      <div className="mt-6 bg-white border rounded-2xl shadow-sm overflow-hidden">
        {/* 테이블 (md 이상) */}
        <div className="hidden md:block">
          <table className="min-w-full table-fixed">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left w-[160px]">{t("\uBC1C\uC0DD\uC2DC\uAC01")}</th>
                <th className="px-4 py-3 text-left w-[90px]">{t("\uD0C0\uC785")}</th>
                <th className="px-4 py-3 text-left w-[110px]">{t("\uACBD\uB85C")}</th>
                <th className="px-4 py-3 text-left">{t("\uBCF4\uB0B8\uCABD (from)")}</th>
                <th className="px-4 py-3 text-left">{t("\uBC1B\uB294\uCABD (to)")}</th>
                <th className="px-4 py-3 text-right w-[140px]">{t("\uAE08\uC561")}</th>
                <th className="px-4 py-3 text-left w-[220px]">{t("\uBA54\uBAA8")}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading && (<tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">{t("\uBD88\uB7EC\uC624\uB294 \uC911\u2026")}</td></tr>)}
              {!loading && empty && (<tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{t("\uD45C\uC2DC\uD560 \uAE30\uB85D\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</td></tr>)}
              {!loading && rows.map((r) => (<tr key={r.id} className="border-t">
                  <td className="px-4 py-3">{fmtDate(r.occurred_at)}</td>
                  <td className="px-4 py-3"><TypeBadge type={r.asset_type}/></td>
                  <td className="px-4 py-3"><ChannelBadge channel={r.channel}/></td>
                  <td className="px-4 py-3">
                    <div className="truncate">
                      <span className="font-medium">{r.from_nickname || r.from_id || '-'}</span>
                      <span className="text-gray-500 ml-1 text-xs">({r.from_user_code ?? '-'})</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="truncate">
                      <span className="font-medium">{r.to_nickname || r.to_id || '-'}</span>
                      <span className="text-gray-500 ml-1 text-xs">({r.to_user_code ?? '-'})</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={Number(r.amount) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {Number(r.amount) >= 0 ? '+' : ''}{fmtInt(r.amount)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="truncate text-gray-600">{r.memo || '-'}</div>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>

        {/* 카드형 (sm ~ md) */}
        <div className="md:hidden divide-y">
          {loading && <div className="p-4 text-center text-gray-500">{t("\uBD88\uB7EC\uC624\uB294 \uC911\u2026")}</div>}
          {!loading && empty && <div className="p-4 text-center text-gray-400">{t("\uD45C\uC2DC\uD560 \uAE30\uB85D\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>}
          {!loading && rows.map((r) => (<div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">{fmtDate(r.occurred_at)}</div>
                <div className="flex items-center gap-2">
                  <TypeBadge type={r.asset_type}/>
                  <ChannelBadge channel={r.channel}/>
                </div>
              </div>
              <div className="mt-2 text-sm">
                <div className="text-gray-700">
                  <span className="font-medium">{r.from_nickname || r.from_id || '-'}</span>
                  <span className="text-gray-500 ml-1">({r.from_user_code ?? '-'})</span>
                  <span className="mx-1 text-gray-400">→</span>
                  <span className="font-medium">{r.to_nickname || r.to_id || '-'}</span>
                  <span className="text-gray-500 ml-1">({r.to_user_code ?? '-'})</span>
                </div>
                <div className="mt-1">
                  <span className={`font-semibold ${Number(r.amount) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {Number(r.amount) >= 0 ? '+' : ''}{fmtInt(r.amount)}
                  </span>
                </div>
                <div className="mt-1 text-gray-600">{r.memo || '-'}</div>
              </div>
            </div>))}
        </div>
      </div>

      {/* 페이지네이션 */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-gray-500">{t("\uCD1D")}{fmtInt(total)}{t("\uAC74")}</div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 rounded-lg border disabled:opacity-50" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}>{t("\uC774\uC804")}</button>
          <div className="px-2">{t("\uD398\uC774\uC9C0")}{page}</div>
          <button className="px-3 py-1 rounded-lg border disabled:opacity-50" disabled={!hasMore || loading} onClick={() => setPage(p => p + 1)}>{t("\uB2E4\uC74C")}</button>
        </div>
      </div>

      {error && (<div className="mt-3 text-sm text-rose-600">
          {error}
        </div>)}
    </div>);
}
